// Лёгкий типизированный fetch-клиент к облачному бэкенду (без axios).
// - берёт базовый URL из import.meta.env.VITE_API_BASE_URL
// - авторизация через httpOnly-cookie (credentials: 'include'), не через JS-токен
// - кидает типизированную ApiError
// - на 401 чистит сессию и уведомляет приложение (через колбэк)

import { clearSession, getMemToken } from '@/auth/storage'

/** Базовый URL бэкенда. В dev можно оставить пустым и ходить через Vite-прокси /api. */
const RAW_BASE = import.meta.env.VITE_API_BASE_URL ?? ''
const API_BASE = RAW_BASE.replace(/\/+$/, '')

export class ApiError extends Error {
  readonly status: number
  readonly code: string | null
  readonly body: unknown

  constructor(status: number, message: string, code: string | null, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.body = body
  }
}

/**
 * Колбэк, который вызывается при 401 (после очистки сессии).
 * AuthProvider регистрирует сюда свой logout/редирект, чтобы клиент
 * не зависел от React.
 */
let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn
}

/** Полезная нагрузка ответа 403 с кодом license_expired. */
export interface LicenseBlockedPayload {
  contacts: string
  expiresAt: number | null
}

/**
 * Колбэк, который вызывается при HTTP 403 с body.code === 'license_expired'.
 * LicenseProvider регистрирует сюда переключатель глобального состояния блокировки,
 * чтобы даже «живой» 403 от любого клубного запроса мгновенно включал локаут.
 */
let onLicenseBlocked: ((payload: LicenseBlockedPayload) => void) | null = null
export function setLicenseBlockedHandler(
  fn: ((payload: LicenseBlockedPayload) => void) | null
): void {
  onLicenseBlocked = fn
}

interface RequestOptions {
  /** query-параметры (undefined/null отбрасываются) */
  query?: Record<string, string | number | boolean | null | undefined>
  signal?: AbortSignal
  /** не подставлять Authorization (например, сам логин) */
  anonymous?: boolean
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  // Если базовый URL не задан — ходим относительно текущего origin через /api
  // (Vite-прокси в dev). new URL требует абсолютную базу, поэтому подставляем origin.
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const rawBase = API_BASE || `${origin}/api`
  const base = rawBase.endsWith('/') ? rawBase : rawBase + '/'
  const url = new URL(path.replace(/^\//, ''), base)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v))
    }
  }
  return url.toString()
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {}
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }

  // Приоритет — httpOnly-cookie (credentials:'include'). Bearer из памяти —
  // fallback на время сессии, если cookie заблокирована браузером.
  const tok = getMemToken()
  if (tok) headers.Authorization = `Bearer ${tok}`

  let payload: BodyInit | undefined
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  let res: Response
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      body: payload,
      credentials: 'include',
      signal: opts.signal
    })
  } catch (cause) {
    throw new ApiError(0, 'Нет связи с сервером', 'network_error', cause)
  }

  if (res.status === 401) {
    clearSession()
    onUnauthorized?.()
    throw new ApiError(401, 'Сессия истекла. Войдите снова.', 'unauthorized', null)
  }

  const isJson = res.headers.get('content-type')?.includes('application/json')
  const parsed: unknown = isJson ? await res.json().catch(() => null) : await res.text()

  if (!res.ok) {
    const msg =
      (isJson && isRecord(parsed) && typeof parsed.message === 'string'
        ? parsed.message
        : null) ?? `Ошибка запроса (${res.status})`
    const code =
      isJson && isRecord(parsed) && typeof parsed.code === 'string' ? parsed.code : null

    // Лицензия клуба истекла: бэкенд отдаёт 403 { code: 'license_expired', … } на
    // любой клубный запрос. Уведомляем приложение (включаем локаут) и всё равно кидаем.
    if (res.status === 403 && code === 'license_expired' && isRecord(parsed)) {
      onLicenseBlocked?.({
        contacts: typeof parsed.contacts === 'string' ? parsed.contacts : '',
        expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : null
      })
    }

    throw new ApiError(res.status, msg, code, parsed)
  }

  return parsed as T
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

/**
 * Отправка multipart/form-data (загрузка картинок).
 *
 * Content-Type здесь НЕ ставится намеренно: его должен проставить браузер сам,
 * дописав boundary. Если задать заголовок вручную, boundary потеряется и сервер
 * не разберёт тело.
 */
async function postForm<T>(path: string, form: FormData, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const tok = getMemToken()
  if (tok) headers.Authorization = `Bearer ${tok}`

  let res: Response
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: 'POST',
      headers,
      body: form,
      credentials: 'include',
      signal: opts.signal
    })
  } catch (cause) {
    throw new ApiError(0, 'Нет связи с сервером', 'network_error', cause)
  }

  if (res.status === 401) {
    clearSession()
    onUnauthorized?.()
    throw new ApiError(401, 'Сессия истекла. Войдите снова.', 'unauthorized', null)
  }

  const isJson = res.headers.get('content-type')?.includes('application/json')
  const parsed: unknown = isJson ? await res.json().catch(() => null) : await res.text()

  if (!res.ok) {
    const msg =
      (isJson && isRecord(parsed) && typeof parsed.message === 'string' ? parsed.message : null) ??
      // 413 отдаёт не наш обработчик, а платформа (nginx/Vercel), и тела с
      // message там может не быть — подставляем понятный текст сами.
      (res.status === 413 ? 'Файл слишком большой' : `Ошибка запроса (${res.status})`)
    throw new ApiError(res.status, msg, null, parsed)
  }

  return parsed as T
}

/**
 * Скачивание бинарного файла (например, акта PDF) с авторизацией — обычная
 * ссылка/window.open не передаст Bearer-токен, поэтому грузим как Blob и
 * отдаём вызывающей стороне создать object URL.
 */
async function getBlob(path: string, opts: RequestOptions = {}): Promise<Blob> {
  const headers: Record<string, string> = {}
  const tok = getMemToken()
  if (tok) headers.Authorization = `Bearer ${tok}`
  let res: Response
  try {
    res = await fetch(buildUrl(path, opts.query), {
      headers,
      credentials: 'include',
      signal: opts.signal
    })
  } catch (cause) {
    throw new ApiError(0, 'Нет связи с сервером', 'network_error', cause)
  }
  if (res.status === 401) {
    clearSession()
    onUnauthorized?.()
    throw new ApiError(401, 'Сессия истекла. Войдите снова.', 'unauthorized', null)
  }
  if (!res.ok) {
    let msg = `Ошибка запроса (${res.status})`
    try {
      const body = (await res.json()) as { message?: string }
      if (body?.message) msg = body.message
    } catch {
      // тело не JSON — оставляем дефолтное сообщение
    }
    throw new ApiError(res.status, msg, null, null)
  }
  return res.blob()
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions): Promise<T> =>
    request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>('PUT', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>('PATCH', path, body, opts),
  del: <T>(path: string, opts?: RequestOptions): Promise<T> =>
    request<T>('DELETE', path, undefined, opts),
  postForm,
  getBlob
}

export type { RequestOptions }
